import React from "react";

/** A number is px; a string is a CSS length as given (`"2rem"`: Tailwind's `h-8` follows the text-size setting). */
const len = (v) => (typeof v === "number" ? `${v}px` : v);

/**
 * #1394 (D221 G4, D354, D357): a button whose drawn control is shorter than 44px, as a transparent
 * 44px tap box with the drawn control inside it. The button is the box (it takes the tap, the key
 * and the focus); `children` are drawn in a span that carries the border, fill and size, so the
 * old screen's pixels are the span's. The equal negative `marginBlock` gives the box's extra height
 * back to the layout, so nothing moves: the drawn control stays where it was (`drawnHeight` is its
 * own height; `spaceAbove` is the margin the caller had above it; each a px number or a CSS length). The focus ring is the span's (`.nw-pill-tap` in index.css), not the box's.
 *
 * Only for a new-look caller. A shared component keeps its own markup for the old look (D217).
 */
export const TapBoxButton = React.forwardRef(function TapBoxButton(
  { drawnHeight = 40, spaceAbove = 0, drawnClassName = "", drawnStyle, className = "", style, children, type = "button", ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={`nw-pill-tap ${className}`.trim()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "var(--nw-size-tap)",
        minWidth: "var(--nw-size-tap)",
        flexShrink: 0,
        marginTop: `calc(${len(spaceAbove)} + (${len(drawnHeight)} - var(--nw-size-tap)) / 2)`,
        marginBottom: `calc((${len(drawnHeight)} - var(--nw-size-tap)) / 2)`,
        padding: 0,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        ...style,
      }}
      {...rest}
    >
      <span className={drawnClassName || undefined} style={drawnStyle}>
        {children}
      </span>
    </button>
  );
});

export default TapBoxButton;

/**
 * The kit's outlined 40px "Try again" as `SecondaryAction` drew it on the record screens' load-failed
 * card (border, radius, navy 13px label), for use as `TapBoxButton`'s drawn span.
 */
export const OUTLINE_RETRY_DRAWN_STYLE = Object.freeze({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  minHeight: 40,
  padding: "0 14px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line-strong)",
  borderRadius: "var(--nw-radius-control)",
  color: "var(--nw-navy)",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  fontWeight: 600,
  fontSize: 13,
});
